import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ProBadge } from '@/components/ProBadge';
import BrandKitScrollRow from "@/components/BrandKitScrollRow";
import { Progress } from "@/components/ui/progress";
import { Upload, X, FileText, Video, Crown, Check, Zap } from "lucide-react";
import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useCreateMyProjectMutation, useMyBrandKitsQuery, useMyDataQuery } from "@/generated/graphql";
import { useToast } from "@/hooks/use-toast";
import { useUploadFile } from "@/hooks/use-upload-file";
import { useTrackProjectCreated } from "@/hooks/use-track-project-created";
import { useHasFeature } from "@/lib/useHasFeature";
import { resolveProcessingMode } from '@/lib/upload/resolveProcessingMode';
import ProcessingModeSelector from '@/components/upload/ProcessingModeSelector';
import { client } from "@/lib/apollo-client";
import { computeProcessingUsage } from "@/lib/quota/computeProcessingUsage";
import { useQuotaUpsellDialog } from "@/hooks/use-quota-upsell-dialog";
import ProcessingUsagePanel from "@/components/ProcessingUsagePanel";
import { detectAudioTrack } from "@/lib/media/detectAudioTrack";

interface NewsPdfUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const NewsPdfUploadModal = ({ isOpen, onClose }: NewsPdfUploadModalProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { dialog: quotaUpsellDialog, openQuotaUpsell, handleQuotaError } = useQuotaUpsellDialog({
    onBeforeUpgrade: onClose,
  });
  const [projectName, setProjectName] = useState("");
  const [selectedBrandKitId, setSelectedBrandKitId] = useState("none");
  const [selectedProcessingMode, setSelectedProcessingMode] = useState("automatic");

  // File states
  const [mp4File, setMp4File] = useState<File | null>(null);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [mp4Duration, setMp4Duration] = useState<number | null>(null);
  const [, setMp4Url] = useState<string | null>(null);
  const [, setPdfUrl] = useState<string | null>(null);

  // Upload progress states
  const [uploadProgress, setUploadProgress] = useState<{ mp4: number; pdf: number } | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // Refs for file inputs
  const mp4InputRef = useRef<HTMLInputElement>(null);
  const pdfInputRef = useRef<HTMLInputElement>(null);

  // Hooks
  const hasProFeatures = useHasFeature('pro_features');
  const { mode: effectiveProcessingMode, automaticLocked } = resolveProcessingMode({
    isPro: hasProFeatures,
    requested: selectedProcessingMode,
  });
  const { getUploadUrl, getVideoDuration } = useUploadFile();
  const { data: brandKitsData, loading: brandKitsLoading } = useMyBrandKitsQuery({
    skip: !hasProFeatures, // Only fetch brand kits for pro users
  });
  const { data: myData, loading: myDataLoading, refetch: refetchMyData } = useMyDataQuery();
  const [createMyProject, { loading: isCreating }] = useCreateMyProjectMutation();
  const trackProjectCreated = useTrackProjectCreated("project", isOpen);

  // Processing-quota usage (shared with the regular project upload). The whole
  // news video is processed, so its duration is the effective processing cost.
  const effectiveDuration = mp4Duration || 0;
  const processingUsage = computeProcessingUsage(myData?.myData?.subscription, effectiveDuration);
  const exceedsProcessingLimit =
    !myDataLoading && mp4Duration != null && processingUsage.exceedsProcessingLimit;

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setProjectName("");
      setMp4File(null);
      setPdfFile(null);
      setMp4Duration(null);
      setMp4Url(null);
      setPdfUrl(null);
      setUploadProgress(null);
      setIsUploading(false);

      // Auto-select default brand kit
      if (brandKitsData?.myBrandKits) {
        const defaultBrandKit = brandKitsData.myBrandKits.find(kit => kit.is_default);
        setSelectedBrandKitId(defaultBrandKit?.id || "none");
      } else {
        setSelectedBrandKitId("none");
      }
    }
  }, [isOpen, brandKitsData?.myBrandKits]);

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  const handleMp4FileSelection = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (file.type !== 'video/mp4') {
      toast({
        title: t('newsPdf.toast.invalidFileType'),
        description: t('newsPdf.toast.onlyMp4'),
        variant: "destructive",
      });
      if (mp4InputRef.current) {
        mp4InputRef.current.value = '';
      }
      return;
    }

    // Get video duration
    let duration: number | null = null;
    try {
      duration = await getVideoDuration(file);
    } catch (error) {
      console.error('Error getting video duration:', error);
    }

    if (!duration) {
      toast({
        title: t('newsPdf.toast.failedToReadVideo'),
        description: t('newsPdf.toast.couldNotDetermineDuration'),
        variant: "destructive",
      });
      if (mp4InputRef.current) {
        mp4InputRef.current.value = '';
      }
      return;
    }

    // A video without audio cannot be transcribed, so processing would fail
    if (await detectAudioTrack(file) === 'absent') {
      toast({
        title: t('errors.videoHasNoAudio'),
        description: t('errors.videoHasNoAudioDescription'),
        variant: "destructive",
      });
      if (mp4InputRef.current) {
        mp4InputRef.current.value = '';
      }
      return;
    }

    setMp4File(file);
    setMp4Duration(duration);

    // Reset file input
    if (mp4InputRef.current) {
      mp4InputRef.current.value = '';
    }
  };

  const handlePdfFileSelection = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (file.type !== 'application/pdf') {
      toast({
        title: t('newsPdf.toast.invalidFileType'),
        description: t('newsPdf.toast.onlyPdf'),
        variant: "destructive",
      });
      if (pdfInputRef.current) {
        pdfInputRef.current.value = '';
      }
      return;
    }

    setPdfFile(file);

    // Reset file input
    if (pdfInputRef.current) {
      pdfInputRef.current.value = '';
    }
  };

  const handleCreateProject = useCallback(async () => {
    if (!mp4File || !pdfFile) {
      toast({
        title: t('newsPdf.toast.requiredFiles'),
        description: t('newsPdf.toast.selectBothFiles'),
        variant: "destructive",
      });
      return;
    }

    // Block before uploading when the video would overrun the monthly quota,
    // mirroring the backend gate so the user isn't left waiting on an upload
    // that will only fail with a generic error.
    if (exceedsProcessingLimit) {
      openQuotaUpsell('processing_time');
      return;
    }

    setIsUploading(true);
    setUploadProgress({ mp4: 0, pdf: 0 });

    try {
      // Upload PDF first
      let pdfUploadUrl: string | null = null;
      try {
        pdfUploadUrl = await getUploadUrl(pdfFile, (progress) => {
          setUploadProgress(prev => prev ? { ...prev, pdf: progress.percentage } : { mp4: 0, pdf: progress.percentage });
        });
      } catch (error) {
        console.error('Error uploading PDF:', error);
        toast({
          title: t('newsPdf.toast.pdfUploadError'),
          description: t('newsPdf.toast.pdfUploadErrorDesc'),
          variant: "destructive",
        });
        setIsUploading(false);
        setUploadProgress(null);
        return;
      }

      if (!pdfUploadUrl) {
        toast({
          title: t('newsPdf.toast.pdfUploadError'),
          description: t('newsPdf.toast.pdfUploadErrorDesc'),
          variant: "destructive",
        });
        setIsUploading(false);
        setUploadProgress(null);
        return;
      }

      // Upload MP4 second
      let mp4UploadUrl: string | null = null;
      try {
        mp4UploadUrl = await getUploadUrl(mp4File, (progress) => {
          setUploadProgress(prev => prev ? { ...prev, mp4: progress.percentage } : { mp4: progress.percentage, pdf: 100 });
        });
      } catch (error) {
        console.error('Error uploading MP4:', error);
        toast({
          title: t('newsPdf.toast.mp4UploadError'),
          description: t('newsPdf.toast.mp4UploadErrorDesc'),
          variant: "destructive",
        });
        setIsUploading(false);
        setUploadProgress(null);
        return;
      }

      if (!mp4UploadUrl) {
        toast({
          title: t('newsPdf.toast.mp4UploadError'),
          description: t('newsPdf.toast.mp4UploadErrorDesc'),
          variant: "destructive",
        });
        setIsUploading(false);
        setUploadProgress(null);
        return;
      }

      // Both files uploaded successfully, now create the project
      const input = {
        name: projectName.trim() || t('newsPdfUploadExtra.defaultProjectName'),
        base_video: {
          url: mp4UploadUrl,
          type: mp4File.type,
          size: mp4File.size,
        },
        news_pdf_mirror: {
          url: pdfUploadUrl,
          type: pdfFile.type,
          size: pdfFile.size,
        },
        ...(selectedBrandKitId && selectedBrandKitId !== "none" && { brand_kit_id: selectedBrandKitId }),
        format: "max_1_min",
        language: "pt-BR",
        prompt: t('newsPdfUpload.defaultPrompt'),
        duration_seconds: 0, // This will be calculated on the backend
        processing_mode: effectiveProcessingMode,
      };

      const result = await createMyProject({
        variables: { input }
      });

      if (result.data?.createMyProject) {
        const newProject = result.data.createMyProject;

        trackProjectCreated();

        // Refetch project list and usage figures
        await client.refetchQueries({ include: ['MyProjects'] });
        await refetchMyData();

        toast({
          title: t('newsPdf.toast.projectCreated'),
          description: t('newsPdf.toast.projectAdded', { name: newProject.name }),
          duration: 5000,
        });

        onClose();
      }
    } catch (error) {
      console.error('Error creating project:', error);
      // Surface the quota-exhausted case with a proper, actionable message
      // instead of the generic error (client pre-check may miss it on a race
      // or when the backend-measured duration differs from the client's).
      if (handleQuotaError(error)) {
        // Dialog is already open.
      } else {
        toast({
          title: t('newsPdf.toast.projectError'),
          description: t('newsPdf.toast.projectErrorDesc'),
          variant: "destructive",
        });
      }
    } finally {
      setIsUploading(false);
      setUploadProgress(null);
    }
  }, [mp4File, pdfFile, projectName, selectedBrandKitId, effectiveProcessingMode, createMyProject, toast, onClose, getUploadUrl, t, exceedsProcessingLimit, effectiveDuration, processingUsage, refetchMyData, openQuotaUpsell, handleQuotaError]);

  const canSubmit = useMemo(() => {
    return (
      mp4File !== null &&
      pdfFile !== null &&
      !isCreating &&
      !isUploading
    );
  }, [mp4File, pdfFile, isCreating, isUploading]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl my-8 bg-background rounded-lg shadow-lg border border-primary/20"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <div className="text-xl sm:text-2xl font-bold bg-gradient-primary bg-clip-text text-transparent">
            {t('newsPdf.title')}
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="p-4 sm:p-6 space-y-4">
          {/* Project Name */}
          <div className="space-y-3">
            <label className="text-sm sm:text-base font-semibold mb-2 flex items-center gap-2 text-foreground">
              <Video className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
              {t('newsPdf.projectName')}
            </label>
            <Input
              placeholder={t('newsPdf.projectNamePlaceholder')}
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              className="h-10 sm:h-12 text-sm sm:text-base shadow-sm focus:shadow-glow border-primary/20 focus:border-primary/40 transition-all duration-300"
            />
          </div>

          {/* PDF Upload */}
          <div className="space-y-3">
            <label className="text-sm sm:text-base font-semibold mb-2 flex items-center gap-2 text-foreground">
              <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
              {t('newsPdf.pdfFile')}
            </label>

            {pdfFile ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="text-sm font-medium text-green-800">{pdfFile.name}</p>
                    <p className="text-xs text-green-600">{formatBytes(pdfFile.size)}</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPdfFile(null)}
                  disabled={isUploading}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <>
                <Button
                  variant="outline"
                  className="w-full h-24 border-dashed border-2 border-primary/40 hover:border-primary"
                  onClick={() => pdfInputRef.current?.click()}
                  disabled={isUploading}
                >
                  <div className="flex flex-col items-center gap-2">
                    <FileText className="w-6 h-6 text-primary" />
                    <span className="text-sm">{t('newsPdf.clickToSelectPdf')}</span>
                  </div>
                </Button>
                <input
                  ref={pdfInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={handlePdfFileSelection}
                  style={{ display: 'none' }}
                />
              </>
            )}
          </div>

          {/* MP4 Upload */}
          <div className="space-y-3">
            <label className="text-sm sm:text-base font-semibold mb-2 flex items-center gap-2 text-foreground">
              <Video className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
              {t('newsPdf.mp4Video')}
            </label>

            {mp4File ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="text-sm font-medium text-green-800">{mp4File.name}</p>
                    <p className="text-xs text-green-600">{formatBytes(mp4File.size)}</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setMp4File(null); setMp4Duration(null); }}
                  disabled={isUploading}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <>
                <Button
                  variant="outline"
                  className="w-full h-24 border-dashed border-2 border-primary/40 hover:border-primary"
                  onClick={() => mp4InputRef.current?.click()}
                  disabled={isUploading}
                >
                  <div className="flex flex-col items-center gap-2">
                    <Upload className="w-6 h-6 text-primary" />
                    <span className="text-sm">{t('newsPdf.clickToSelectMp4')}</span>
                  </div>
                </Button>
                <input
                  ref={mp4InputRef}
                  type="file"
                  accept=".mp4,video/mp4"
                  onChange={handleMp4FileSelection}
                  style={{ display: 'none' }}
                />
              </>
            )}
          </div>

          {/* Brand Kit Selection */}
          <div className="space-y-3">
            <label className="text-sm sm:text-base font-semibold mb-2 flex items-center gap-2 text-foreground">
              <Crown className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
              {t('newsPdf.brandKitOptional')}
              <ProBadge />
            </label>
            {brandKitsLoading && hasProFeatures ? (
              <div className="h-10 sm:h-12 border border-primary/20 rounded-md flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                <span className="ml-2 text-sm text-muted-foreground">{t('newsPdf.loadingBrandKits')}</span>
              </div>
            ) : hasProFeatures ? (
              <div className="space-y-3">
                <div
                  className={`cursor-pointer rounded-lg border-2 transition-colors p-2 text-center text-sm ${
                    selectedBrandKitId === "none"
                      ? 'border-primary bg-primary/10'
                      : 'border-gray-600 hover:border-gray-400'
                  }`}
                  onClick={() => setSelectedBrandKitId("none")}
                >
                  {t('newsPdf.noBrandKit')}
                </div>
                {(brandKitsData?.myBrandKits || []).length > 0 && (
                  <BrandKitScrollRow
                    kits={brandKitsData?.myBrandKits || []}
                    selectedId={selectedBrandKitId}
                    onSelect={setSelectedBrandKitId}
                    showDefault
                  />
                )}
              </div>
            ) : (
              <div className="opacity-60 cursor-not-allowed bg-muted rounded-md p-3 text-sm text-center text-muted-foreground">
                {t('newsPdf.brandKitProOnly')}
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              {hasProFeatures
                ? t('newsPdf.brandKitTip')
                : t('newsPdf.brandKitProOnly')
              }
            </p>
          </div>

          {/* Brand Kit Suggestion for users with no brand kits */}
          {hasProFeatures && !brandKitsLoading && brandKitsData?.myBrandKits?.length === 0 && (
            <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl p-4 border border-primary/20 shadow-sm">
              <div className="flex items-start gap-3">
                <Crown className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-foreground mb-1">
                    {t('newsPdf.createFirstBrandKit')}
                  </h3>
                  <p className="text-xs text-muted-foreground mb-3">
                    {t('newsPdf.brandKitDescription')}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs bg-primary/10 text-primary border-primary/30 hover:bg-primary/20"
                    onClick={() => window.open('/templates', '_blank')}
                  >
                    <Crown className="w-3 h-3 mr-1" />
                    {t('newsPdf.createBrandKit')}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Processing Mode Selector */}
          <div className="space-y-3">
            <label className="text-sm sm:text-base font-semibold mb-2 flex items-center gap-2 text-foreground">
              <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
              {t('copyLibrary.processingMode')}
            </label>
            <ProcessingModeSelector
              value={effectiveProcessingMode}
              onChange={setSelectedProcessingMode}
              automaticLocked={automaticLocked}
            />
          </div>

          {/* Upload Progress */}
          {uploadProgress && (
            <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl p-4 border border-primary/20">
              <div className="space-y-3">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold">{t('newsPdf.uploadingPdf')}</span>
                    <span className="text-sm font-medium">{uploadProgress.pdf.toFixed(1)}%</span>
                  </div>
                  <Progress value={uploadProgress.pdf} className="h-2" />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold">{t('newsPdf.uploadingMp4')}</span>
                    <span className="text-sm font-medium">{uploadProgress.mp4.toFixed(1)}%</span>
                  </div>
                  <Progress value={uploadProgress.mp4} className="h-2" />
                </div>
              </div>
            </div>
          )}

          {/* Plan Usage (shared with the regular project upload) */}
          <ProcessingUsagePanel
            loading={myDataLoading}
            usage={processingUsage}
            effectiveDurationSeconds={effectiveDuration}
            hasVideo={mp4Duration != null}
            exceedsProcessingLimit={exceedsProcessingLimit}
          />

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row justify-between pt-6 gap-3 sm:gap-4">
            <Button variant="outline" onClick={onClose} size="lg" className="flex-1" disabled={isCreating || isUploading}>
              {t('newsPdf.cancel')}
            </Button>
            <Button
              variant="gradient"
              size="lg"
              className="flex-1"
              onClick={handleCreateProject}
              disabled={!canSubmit}
            >
              {isUploading
                ? t('newsPdf.uploadingFiles')
                : isCreating
                  ? t('newsPdf.creating')
                  : t('newsPdf.createProject')}
            </Button>
          </div>
          {quotaUpsellDialog}
        </div>
      </div>
    </div>
  );
};

export default NewsPdfUploadModal;

